<template>
  <div class="d-flex flex-column pa-4 ga-4">
    <RichText
      v-model="store.plan.context_general_information"
      label="3.1 Context & General Information"
      required
      :readonly="readonly"
    />

    <v-card variant="outlined" rounded="lg">
      <v-toolbar density="compact">
        <v-toolbar-title>3.2 Boundary Limits</v-toolbar-title>
      </v-toolbar>

      <div v-if="!readonly" class="d-flex align-center pa-2">
        <div class="d-flex flex-column ga-2" style="width: 600px">
          <v-autocomplete
            label="Cost Center"
            v-model="searchCostCenters"
            :items="costCenters"
            :clearable="true"
            :loading="fetchingCostCenters"
            variant="outlined"
            density="compact"
            hide-details
            item-value="id"
            item-title="name"
            multiple
            return-object
            @update:model-value="fetchPlannerGroups"
          />
          <v-autocomplete
            label="Planner Group"
            v-model="searchPlanners"
            :items="plannerGroups"
            :clearable="true"
            :loading="fetchingPlannerGroups"
            variant="outlined"
            density="compact"
            hide-details
            item-value="id"
            item-title="name"
            multiple
            return-object
            @update:model-value="fetchCostCenters"
          />
          <FLOCSearch
            label="Functional Location"
            v-model="flocs"
            :flocs="
              store.plan.floc_boundaries.map((b) => b.functional_location)
            "
            :cost-centers="searchCostCenters"
            :planner-groups="searchPlanners"
            clearable
            variant="outlined"
            multiple
            density="compact"
            hide-details
          />
        </div>
        <v-spacer />
        <v-btn
          :disabled="flocs.length === 0"
          color="primary"
          class="mt-2 mb-2"
          variant="text"
          @click="addBoundaries"
        >
          <v-icon>mdi-plus</v-icon>Add
        </v-btn>
      </div>

      <v-table fixed-header density="compact" class="boundary-limits-table">
        <thead>
          <tr>
            <th class="text-left">Functional Location</th>
            <th v-if="!readonly" class="text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in store.plan.floc_boundaries" :key="item.id">
            <td>{{ item.functional_location.functional_location }}</td>
            <td v-if="!readonly" class="text-right">
              <v-btn
                icon="mdi-delete"
                color="red"
                variant="plain"
                @click="deleteBoundary(item)"
              />
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-card variant="outlined" rounded="lg">
      <v-toolbar density="compact">
        <v-toolbar-title>3.3 Boundary Limits Tree</v-toolbar-title>
      </v-toolbar>
      <FunctionalLocationTree
        ref="flocTree"
        :tree-items="sortedTreeItems"
        max-height="500px"
        @view="flocDialog?.open($event.item.id)"
      />
      <FunctionalLocationDialog ref="flocDialog" readonly />
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { FunctionalLocation, PlannerGroup, CostCenter } from "@/classes";
import { Deleteable, FLOCBoundary } from "@/classes/asset-management-plan";

import RichText from "@/components/base/inputs/RichText.vue";
import { computed, onMounted, ref } from "vue";
import { useAMPStore } from "@/store/asset-management-plan";
import FLOCSearch from "../FLOCSearch.vue";
import FunctionalLocationTree from "@/components/functional-location/tree/FunctionalLocationTree.vue";
import { TreeItem } from "@/components/functional-location/tree/tree";
import FunctionalLocationDialog from "@/components/functional-location/FunctionalLocationDialog.vue";

const props = defineProps<{
  readonly: boolean;
}>();

const emit = defineEmits<{
  (e: "delete", val: Deleteable): void;
}>();

const store = useAMPStore();

const flocs = ref<FunctionalLocation[]>([]);
const searchCostCenters = ref<CostCenter[]>([]);
const searchPlanners = ref<PlannerGroup[]>([]);

const plannerGroups = ref<PlannerGroup[]>([]);
const costCenters = ref<CostCenter[]>([]);

const flocTree = ref<InstanceType<typeof FunctionalLocationTree> | null>(null);
const flocDialog = ref<InstanceType<typeof FunctionalLocationDialog> | null>(
  null,
);
const treeItems = ref<TreeItem<FunctionalLocation>[]>([]);

// Display tree items in same order as boundary list
const sortedTreeItems = computed(() => {
  const items: TreeItem<FunctionalLocation>[] = [];
  store.plan.floc_boundaries.forEach((bound) => {
    const item = treeItems.value.find(
      (i) => i.item.id === bound.functional_location_id,
    );
    if (item) items.push(item);
  });
  return items;
});

onMounted(() => {
  fetchPlannerGroups();
  fetchCostCenters();
  refreshTreeItems();
});

const refreshTreeItems = async () => {
  const flocIds: string[] = [];
  for (const bound of store.plan.floc_boundaries) {
    const exist = treeItems.value.find(
      (item) => item.item.id === bound.functional_location_id,
    );
    if (!exist) flocIds.push(bound.functional_location_id);
  }
  const flocs = await FunctionalLocation.getTreeChildren({
    parent_id: null,
    floc_ids: flocIds,
  });
  for (const floc of flocs) {
    const item = {
      item: floc,
      expanded: false,
      children: null,
    } as TreeItem<FunctionalLocation>;
    treeItems.value.push(item);
  }
};

const fetchingPlannerGroups = ref(false);
const fetchPlannerGroups = async () => {
  fetchingPlannerGroups.value = true;
  plannerGroups.value = await PlannerGroup.filters(
    {
      cost_center_ids: searchCostCenters.value.map((c) => c.id),
    },
    "functional-location/planner-group",
  );
  fetchingPlannerGroups.value = false;
};

const fetchingCostCenters = ref(false);
const fetchCostCenters = async () => {
  fetchingCostCenters.value = true;
  costCenters.value = await CostCenter.filters(
    {
      planner_group_ids: searchPlanners.value.map((p) => p.id),
    },
    "functional-location/cost-center",
  );
  fetchingCostCenters.value = false;
};

const addBoundaries = () => {
  flocs.value.forEach((floc) => {
    const boundary = new FLOCBoundary();
    boundary.asset_management_plan = store.plan;
    boundary.asset_management_plan_id = store.plan.id;
    boundary.functional_location = floc;
    boundary.functional_location_id = floc.id;
    store.plan.floc_boundaries.push(boundary);
  });
  flocs.value = [];
  refreshTreeItems();
  store.getPreventativeMaintenance();
};

const deleteBoundary = (boundary: FLOCBoundary) => {
  store.plan.floc_boundaries = store.plan.floc_boundaries.filter(
    (b) => b.id !== boundary.id,
  );
  treeItems.value = treeItems.value.filter(
    (item) => item.item.id !== boundary.functional_location_id,
  );
  store.getPreventativeMaintenance();
  emit("delete", boundary);
};
</script>

<style lang="scss" scoped>
.boundary-limits-table {
  max-height: 500px;
}
</style>
